import { cn } from '@moment/design-system-ui/lib/cn'
import { useEffect, useRef, useState } from 'react'
import zxcvbn from 'zxcvbn'

import { Text } from '#components/controls/text'
import {
  CompromisedPasswordWarning,
  PasswordRequirementsContainer,
  RequirementItem,
  RequirementsChecklistContainer,
  StrengthMeterBar,
  StrengthMeterBarsContainer,
  StrengthMeterContainer,
  StrengthTextContainer,
} from '#components/shared/styles'

import {
  type CheckCompromisedPassword,
  checkCompromisedPassword as defaultCheckCompromisedPassword,
} from './check-compromised-password'

export {
  type CheckCompromisedPassword,
  type CompromisedPasswordResult,
  checkCompromisedPassword,
} from './check-compromised-password'

// Constants
const MIN_PASSWORD_LENGTH = 12
const MIN_COMPLEXITY_SCORE = 3
const DEBOUNCE_DELAY = 500

const HAS_LOWERCASE = /[a-z]/
const HAS_UPPERCASE = /[A-Z]/
const HAS_NUMBER = /[0-9]/
const HAS_SPECIAL = /[!@#$%^&*(),.?":{}|<>]/

const PasswordStrength = {
  None: '',
  Weak: 'weak',
  Fair: 'fair',
  Strong: 'strong',
} as const

type PasswordStrength = (typeof PasswordStrength)[keyof typeof PasswordStrength]

const DEFAULT_PASSWORD_REQUIREMENTS_STATE = {
  minLength: false,
  hasUpperAndLower: false,
  hasNumber: false,
  hasSpecial: false,
}

interface PasswordRequirementsProps {
  password: string
  showRequirements?: boolean
  className?: string
  onRequirementsChange?: (allMet: boolean, score: number) => void
  checkCompromisedPassword?: CheckCompromisedPassword
}

export const PasswordRequirements = ({
  password,
  showRequirements = true,
  className,
  onRequirementsChange,
  checkCompromisedPassword = defaultCheckCompromisedPassword,
}: PasswordRequirementsProps) => {
  const [requirements, setRequirements] = useState(
    DEFAULT_PASSWORD_REQUIREMENTS_STATE,
  )
  const [strengthLabel, setStrengthLabel] = useState<PasswordStrength>(
    PasswordStrength.None,
  )
  const [strengthScore, setStrengthScore] = useState(0)
  const [isCompromised, setIsCompromised] = useState(false)
  const [compromisedCount, setCompromisedCount] = useState(0)

  // Keep the latest checker in a ref so the debounced effect depends only on
  // `password`, not on the checker's identity (which may be an inline default).
  const checkCompromisedPasswordRef = useRef(checkCompromisedPassword)
  checkCompromisedPasswordRef.current = checkCompromisedPassword

  // Password validation and strength calculation
  useEffect(() => {
    if (password) {
      const newRequirements = {
        minLength: password.length >= MIN_PASSWORD_LENGTH,
        hasUpperAndLower:
          HAS_LOWERCASE.test(password) && HAS_UPPERCASE.test(password),
        hasNumber: HAS_NUMBER.test(password),
        hasSpecial: HAS_SPECIAL.test(password),
      }
      setRequirements(newRequirements)

      const result = zxcvbn(password)
      setStrengthScore(result.score)

      if (result.score < 2) {
        setStrengthLabel(PasswordStrength.Weak)
      } else if (result.score < 3) {
        setStrengthLabel(PasswordStrength.Fair)
      } else {
        setStrengthLabel(PasswordStrength.Strong)
      }
    } else {
      setRequirements(DEFAULT_PASSWORD_REQUIREMENTS_STATE)
      setStrengthLabel(PasswordStrength.None)
      setStrengthScore(0)
    }
  }, [password])

  // Debounced compromised-password check
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>
    let isCancelled = false

    const checkCompromised = async () => {
      if (password && password.length >= MIN_PASSWORD_LENGTH) {
        const compromisedResult =
          await checkCompromisedPasswordRef.current(password)

        if (!isCancelled) {
          if (compromisedResult.tag === 'Pwned') {
            setIsCompromised(true)
            setCompromisedCount(compromisedResult.occurrences || 0)
          } else {
            // NotFound or Error - don't block the user
            setIsCompromised(false)
            setCompromisedCount(0)
          }
        }
      } else {
        setIsCompromised(false)
        setCompromisedCount(0)
      }
    }

    if (password && password.length >= MIN_PASSWORD_LENGTH) {
      timeoutId = setTimeout(checkCompromised, DEBOUNCE_DELAY)
    } else {
      setIsCompromised(false)
      setCompromisedCount(0)
    }

    return () => {
      isCancelled = true
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [password])

  // Notify parent of requirement status
  useEffect(() => {
    const allRequirementsMet = Object.values(requirements).every(Boolean)
    const meetsComplexity = strengthScore >= MIN_COMPLEXITY_SCORE

    onRequirementsChange?.(
      allRequirementsMet && meetsComplexity && !isCompromised,
      strengthScore,
    )
  }, [requirements, strengthScore, isCompromised, onRequirementsChange])

  if (!showRequirements) {
    return null
  }

  // Get bar colors based on strength score
  const getBarColors = (barIndex: number) => {
    const allRequirementsMet = Object.values(requirements).every(Boolean)

    if (
      allRequirementsMet &&
      strengthScore >= MIN_COMPLEXITY_SCORE &&
      !isCompromised
    ) {
      return 'bg-(--color-border-primary-contrast) dark:bg-(--color-surface-alternative)'
    }

    if (strengthScore === 0) {
      return ''
    }
    if (strengthScore === 1) {
      return barIndex === 0
        ? 'bg-(--color-border-primary-contrast)/60 dark:bg-(--color-border-primary-contrast)'
        : ''
    }
    if (strengthScore === 2) {
      return barIndex < 2
        ? 'bg-(--color-border-primary-contrast)/60 dark:bg-(--color-border-primary-contrast)'
        : ''
    }
    return 'bg-(--color-border-primary-contrast)/60 dark:bg-(--color-border-primary-contrast)'
  }

  const getStrengthTextColor = () =>
    strengthLabel === PasswordStrength.Strong
      ? 'text-(--color-text-primary) dark:text-(--color-text-primary-contrast)'
      : 'text-(--color-text-secondary) dark:text-(--color-text-secondary-contrast)'

  return (
    <PasswordRequirementsContainer className={className}>
      {isCompromised && (
        <CompromisedPasswordWarning>
          <Text variant="base" className="text-red-500 dark:text-red-400">
            The provided password was found {compromisedCount.toLocaleString()}{' '}
            times in data breaches and is not safe to be used.
          </Text>
        </CompromisedPasswordWarning>
      )}

      <RequirementsChecklistContainer>
        <RequirementItem
          met={requirements.minLength}
          text={`A minimum of ${MIN_PASSWORD_LENGTH} characters`}
        />
        <RequirementItem
          met={requirements.hasUpperAndLower}
          text="Lower and uppercase letters"
        />
        <RequirementItem
          met={requirements.hasNumber}
          text="At least 1 number"
        />
        <RequirementItem
          met={requirements.hasSpecial}
          text={
            <>
              At least 1 symbol e.g.{' '}
              <span className="tracking-[.07em] ml-0.25">!@#$%</span>
            </>
          }
        />
      </RequirementsChecklistContainer>

      <StrengthMeterContainer>
        <StrengthMeterBarsContainer>
          {[0, 1, 2].map((index) => (
            <StrengthMeterBar key={index} className={getBarColors(index)} />
          ))}
        </StrengthMeterBarsContainer>

        <StrengthTextContainer>
          <Text
            variant="base"
            className={cn(
              'text-(--color-text-tertiary) dark:text-(--color-text-secondary-contrast)',
              'px-0.25',
            )}
          >
            Password strength:
          </Text>
          <Text
            variant="base-semibold"
            className={cn(
              password
                ? getStrengthTextColor()
                : 'text-(--color-text-secondary) dark:text-(--color-text-secondary-contrast)',
            )}
          >
            {password
              ? strengthLabel.charAt(0).toUpperCase() + strengthLabel.slice(1)
              : 'Enter password'}
          </Text>
        </StrengthTextContainer>
      </StrengthMeterContainer>
    </PasswordRequirementsContainer>
  )
}
